import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { COLORS, FONTS, slideFromRightVariant } from "./Hero.styles";

/**
 * HeroVisualization
 *
 * Signature element: a clean architecture stack representing how requests
 * flow through the platform. Five layers, top to bottom:
 *   Enterprise Apps → AI Agents → LLM Layer → Knowledge Base → Enterprise Data
 *
 * Design choices:
 *  - Each layer is a glass card with a label + one-line description (real
 *    content, not decoration — encodes the actual request lifecycle).
 *  - A single vertical connector line runs through all layers; a small dot
 *    travels along it on loop to suggest "live" data flow without being
 *    distracting (one element animates, not five).
 *  - No SVG neural net, no particles. Pure layered cards + one moving dot.
 *  - All motion is transform/opacity only — GPU compositable.
 */



/**
 * HeroVisualization
 *
 * Signature element: a clean architecture stack representing how requests
 * flow through the platform. Five layers, top to bottom:
 *   Enterprise Apps → AI Agents → LLM Layer → Knowledge Base → Enterprise Data
 *
 * Design choices:
 *  - Each layer is a glass card with a label + one-line description (real
 *    content, not decoration — encodes the actual request lifecycle).
 *  - A single vertical connector line runs through all layers; a small dot
 *    travels along it on loop to suggest "live" data flow without being
 *    distracting (one element animates, not five).
 *  - No SVG neural net, no particles. Pure layered cards + one moving dot.
 *  - All motion is transform/opacity only — GPU compositable.
 */



/**
 * HeroVisualization
 *
 * Signature element: a clean architecture stack representing how requests
 * flow through the platform. Five layers, top to bottom:
 *   Enterprise Apps → AI Agents → LLM Layer → Knowledge Base → Enterprise Data
 *
 * Design choices:
 *  - Each layer is a glass card with a label + one-line description (real
 *    content, not decoration — encodes the actual request lifecycle).
 *  - A single vertical connector line runs through all layers; a small dot
 *    travels along it on loop to suggest "live" data flow without being
 *    distracting (one element animates, not five).
 *  - No SVG neural net, no particles. Pure layered cards + one moving dot.
 *  - All motion is transform/opacity only — GPU compositable.
 */

const LAYERS = [
  { label: "Enterprise Apps",  desc: "CRM, ERP, support, internal tools" },
  { label: "AI Agents",        desc: "Task orchestration & decisioning" },
  { label: "LLM Layer",        desc: "Reasoning, generation, multilingual" },
  { label: "Knowledge Base",   desc: "RAG retrieval & embeddings" },
  { label: "Enterprise Data",  desc: "Structured & unstructured sources" },
];

const ROW_HEIGHT = 64;
const ROW_GAP = 14;

export default function HeroVisualization() {
  const totalHeight = LAYERS.length * ROW_HEIGHT + (LAYERS.length - 1) * ROW_GAP;

  return (
    <motion.div {...slideFromRightVariant(0.2)}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: { xs: "440px", lg: "480px" },
          mx: { xs: "auto", lg: 0 },
        }}
      >
        {/* Panel header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            mb: "18px",
            px: "4px",
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: COLORS.primary,
            }}
          />
          <Typography
            sx={{
              fontFamily: FONTS.body,
              fontSize: "0.72rem",
              fontWeight: 500,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: COLORS.textFaint,
            }}
          >
            Platform Architecture
          </Typography>
        </Box>

        {/* Stack container */}
        <Box
          sx={{
            position: "relative",
            borderRadius: "18px",
            border: `1px solid ${COLORS.border}`,
            background: "rgba(255,255,255,0.02)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            p: { xs: "18px", sm: "22px" },
          }}
        >
          {/* Connector line + traveling dot, positioned behind the layer cards */}
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              // Dot center from panel's left edge (absolute origin):
              //   panel border (1px) + panel padding + card dot_left + half dot width (4.5px)
              // xs: 1 + 18 + 18 + 4.5 = 41.5px
              // sm: 1 + 22 + 20 + 4.5 = 47.5px
              top: { xs: "18px", sm: "22px" },
              left: { xs: "41.5px", sm: "47.5px" },
              width: "1px",
              height: `${totalHeight}px`,
              background:
                "linear-gradient(to bottom, transparent, rgba(30,144,255,0.35) 12%, rgba(30,144,255,0.35) 88%, transparent)",
            }}
          >
            <Box
              component={motion.div}
              animate={{ top: ["0%", "100%"] }}
              transition={{
                duration: 4.5,
                repeat: Infinity,
                ease: "easeInOut",
                repeatType: "loop",
              }}
              sx={{
                position: "absolute",
                left: "-2.5px",
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: COLORS.primary,
                boxShadow: `0 0 8px ${COLORS.primary}`,
                "@media (prefers-reduced-motion: reduce)": {
                  animation: "none",
                  top: "50% !important",
                },
              }}
            />
          </Box>

          {/* Layer rows */}
          <Box sx={{ position: "relative", display: "flex", flexDirection: "column", gap: `${ROW_GAP}px` }}>
            {LAYERS.map((layer, i) => (
              <Box
                key={layer.label}
                component={motion.div}
                initial={{ opacity: 0, x: 16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  height: `${ROW_HEIGHT}px`,
                  pl: { xs: "44px", sm: "48px" },
                  pr: "16px",
                  borderRadius: "12px",
                  border: `1px solid ${COLORS.border}`,
                  background: "rgba(255,255,255,0.03)",
                  transition: "border-color 0.2s ease, background-color 0.2s ease",
                  "&:hover": {
                    borderColor: COLORS.borderHover,
                    background: "rgba(30,144,255,0.04)",
                  },
                }}
              >
                {/* Node dot on the connector line */}
                <Box
                  sx={{
                    position: "absolute",
                    left: { xs: "18px", sm: "20px" },
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: 9,
                    height: 9,
                    borderRadius: "50%",
                    background: "#000",
                    border: `1.5px solid ${COLORS.primary}`,
                  }}
                />

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: FONTS.body,
                      fontWeight: 600,
                      fontSize: "0.88rem",
                      color: COLORS.white,
                      lineHeight: 1.3,
                    }}
                  >
                    {layer.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: FONTS.body,
                      fontWeight: 400,
                      fontSize: "0.74rem",
                      color: COLORS.textFaint,
                      lineHeight: 1.4,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {layer.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
}